import { useMemo, useState } from "react";
import { Message } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isHttpClientError, isGenericHttpFailureMessage } from "../../../lib/http/errors";
import type { GeoBusinessVerifyResult, GeoOperationStatus } from "../../../shared/business-geo";
import { crmOrganizationDefaultListParams } from "../config";
import {
  createCrmOrganization,
  createCrmOrganizationSite,
  deleteCrmOrganization,
  deleteCrmOrganizationSite,
  discoverCrmOrganizationArtUnits,
  fetchCrmOrganizationDetail,
  fetchCrmOrganizationList,
  retryCrmOrganizationCampusDiscovery,
  resolveCrmOrganizationGeo,
  updateCrmOrganization,
  updateCrmOrganizationSite,
} from "../repo/crmOrganizationRepo";
import {
  applyGeoResolveResultToOrganizationMutation,
  applyGeoResolveResultToSiteMutation,
  buildGeoResolveAreaName,
  normalizeCrmOrganizationFilters,
  resolvedCrmOrganizationSiteGeoStatus,
  savedCrmOrganizationSiteGeoResult,
} from "../service/crmOrganizationService";
import type {
  CrmOrganizationItem,
  CrmOrganizationListFilters,
  CrmOrganizationMutation,
  CrmOrganizationSiteItem,
  CrmOrganizationSiteMutation,
} from "../types";

const crmOrganizationQueryRootKey = ["crm_organization", "list"] as const;
const crmOrganizationDefaultFilters: CrmOrganizationListFilters = {
  orgType: "高校",
};

export const emptyCrmOrganizationMutation: CrmOrganizationMutation = {
  orgName: "",
  orgType: "高校",
  orgSubtype: "本科",
  province: "",
  city: "",
  district: "",
  address: "",
  isDisabled: false,
  remark: "",
};

export const emptyCrmOrganizationSiteMutation: CrmOrganizationSiteMutation = {
  siteName: "",
  siteType: "校区",
  province: "",
  city: "",
  district: "",
  address: "",
  lng: undefined,
  lat: undefined,
  geoStatus: "unverified",
  isMainSite: false,
  isDisabled: false,
  remark: "",
};

/** 管理学校组织列表页查询、分页、学校维护与校区维护行为。 */
export function useCrmOrganizationRuntime(initialKeyword?: string) {
  const queryClient = useQueryClient();
  const initialFilters = initialKeyword
    ? { ...crmOrganizationDefaultFilters, keyword: initialKeyword }
    : crmOrganizationDefaultFilters;
  const [draftFilters, setDraftFilters] =
    useState<CrmOrganizationListFilters>(initialFilters);
  const [submittedFilters, setSubmittedFilters] =
    useState<CrmOrganizationListFilters>(initialFilters);
  const [page, setPage] = useState(crmOrganizationDefaultListParams.page);
  const [pageSize, setPageSize] = useState(crmOrganizationDefaultListParams.pageSize);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [geoFeedbackMessage, setGeoFeedbackMessage] = useState("");
  const [geoStatus, setGeoStatus] = useState<GeoOperationStatus>("idle");
  const [geoResult, setGeoResult] = useState<GeoBusinessVerifyResult>();
  const [geoOwnerSiteId, setGeoOwnerSiteId] = useState<number>();
  const [geoAdopted, setGeoAdopted] = useState(false);
  const [campusDiscoverySummary, setCampusDiscoverySummary] = useState<CrmOrganizationItem["campusDiscovery"]>();
  const [campusDiscoveryOrganizationId, setCampusDiscoveryOrganizationId] = useState<number>();

  const listParams = useMemo(
    () => ({
      ...crmOrganizationDefaultListParams,
      ...submittedFilters,
      page,
      pageSize,
    }),
    [page, pageSize, submittedFilters],
  );

  const listQuery = useQuery({
    queryKey: [...crmOrganizationQueryRootKey, listParams],
    queryFn: () => fetchCrmOrganizationList(listParams),
  });

  const detailQuery = useQuery({
    queryKey: ["crm_organization", "detail", selectedOrganizationId],
    queryFn: () => fetchCrmOrganizationDetail(selectedOrganizationId ?? 0),
    enabled: selectedOrganizationId !== null,
  });

  const listPage = listQuery.data ?? {
    items: [],
    page,
    pageSize,
    total: 0,
  };

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: crmOrganizationQueryRootKey,
    });
  const invalidateSelectedDetail = () =>
    queryClient.invalidateQueries({
      queryKey: ["crm_organization", "detail", selectedOrganizationId],
    });

  const createMutation = useMutation({
    mutationFn: createCrmOrganization,
    onSuccess: (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      return invalidateList();
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ item, payload }: { item: CrmOrganizationItem; payload: CrmOrganizationMutation }) =>
      updateCrmOrganization(item.id, payload),
    onSuccess: (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      return invalidateList();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (item: CrmOrganizationItem) => deleteCrmOrganization(item.id),
    onSuccess: invalidateList,
  });
  const createSiteMutation = useMutation({
    mutationFn: (payload: CrmOrganizationSiteMutation) =>
      createCrmOrganizationSite(selectedOrganizationId ?? 0, payload),
    onSuccess() {
      invalidateList();
      invalidateSelectedDetail();
    },
  });
  const updateSiteMutation = useMutation({
    mutationFn: ({ site, payload }: { site: CrmOrganizationSiteItem; payload: CrmOrganizationSiteMutation }) =>
      updateCrmOrganizationSite(selectedOrganizationId ?? 0, site.id, payload),
    onSuccess() {
      invalidateList();
      invalidateSelectedDetail();
    },
  });
  const deleteSiteMutation = useMutation({
    mutationFn: (site: CrmOrganizationSiteItem) =>
      deleteCrmOrganizationSite(selectedOrganizationId ?? 0, site.id),
    onSuccess() {
      invalidateList();
      invalidateSelectedDetail();
    },
  });
  const geoResolveMutation = useMutation({
    mutationFn: resolveCrmOrganizationGeo,
  });
  const campusDiscoveryMutation = useMutation({
    mutationFn: retryCrmOrganizationCampusDiscovery,
    onSuccess(summary) {
      setCampusDiscoverySummary(summary);
      invalidateList();
      invalidateSelectedDetail();
    },
  });

  return {
    draftFilters,
    items: listPage.items,
    loading: listQuery.isLoading,
    page,
    pageSize,
    total: listPage.total,
    selectedOrganization: detailQuery.data ?? null,
    selectedOrganizationId,
    selectedOrganizationLoading: detailQuery.isLoading,
    mutationErrorMessage,
    geoFeedbackMessage,
    geoResolving: geoResolveMutation.isPending,
    geoStatus,
    geoResult,
    campusDiscoverySummary,
    campusDiscoveryOrganizationId,
    campusDiscoveryRetrying: campusDiscoveryMutation.isPending,
    errorMessage: listQuery.error ? "学校组织列表加载失败，请稍后重试。" : "",
    setDraftFilters,
    setSelectedOrganizationId,
    clearMutationError() {
      setMutationErrorMessage("");
    },
    clearGeoFeedback() {
      setGeoFeedbackMessage("");
      setGeoStatus("idle");
      setGeoResult(undefined);
      setGeoOwnerSiteId(undefined);
      setGeoAdopted(false);
    },
    hydrateSavedSiteGeo(site?: CrmOrganizationSiteItem) {
      if (site && geoOwnerSiteId === site.id && geoStatus !== "idle") {
        return;
      }
      const snapshot = site ? savedCrmOrganizationSiteGeoResult(site) : undefined;
      setGeoStatus(snapshot ? "confirmed" : "idle");
      setGeoResult(snapshot);
      setGeoOwnerSiteId(snapshot ? site?.id : undefined);
      setGeoAdopted(false);
    },
    markGeoCandidateAdopted() {
      setGeoAdopted(true);
    },
    handleSearch() {
      setSubmittedFilters(normalizeCrmOrganizationFilters(draftFilters));
      setPage(1);
    },
    handleReset() {
      setDraftFilters(crmOrganizationDefaultFilters);
      setSubmittedFilters(crmOrganizationDefaultFilters);
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    async handleCreateOrganization(payload: CrmOrganizationMutation) {
      const created = await runMutation(
        () => createMutation.mutateAsync(payload),
        setMutationErrorMessage,
      );
      if (created.orgType !== "高校" || created.isDisabled) {
        return;
      }
      Message.info("学校已保存，正在后台自动发现校区与艺术相关组织单元…");
      setCampusDiscoveryOrganizationId(created.id);
      void retryCrmOrganizationCampusDiscovery(created.id)
        .then((summary) => {
          setCampusDiscoverySummary(summary);
          invalidateList();
          if (summary.createdCount > 0) {
            Message.success(`已自动新增 ${summary.createdCount} 个校区。`);
            return;
          }
          if (summary.status === "retryable_failure") {
            Message.warning("校区自动发现失败，可在校区维护中重试。");
            return;
          }
          if (summary.pendingCount > 0) {
            Message.info("校区发现完成，有待确认项，请在校区维护中处理。");
          }
        })
        .catch(() => {
          Message.warning("校区自动发现失败，可在校区维护中重试。");
        });
      void discoverCrmOrganizationArtUnits(created.id)
        .then((unitDiscovery) => {
          if (unitDiscovery.createdCount > 0) {
            Message.success(
              `已自动新增 ${unitDiscovery.createdCount} 个艺术相关组织单元。`,
            );
            return;
          }
          if (unitDiscovery.status === "retryable_failure") {
            Message.warning(
              "艺术相关组织单元自动发现失败，可稍后在组织单元页手工维护。",
            );
          }
        })
        .catch(() => {
          Message.warning(
            "艺术相关组织单元自动发现失败，可稍后在组织单元页手工维护。",
          );
        });
    },
    async handleUpdateOrganization(item: CrmOrganizationItem, payload: CrmOrganizationMutation) {
      await runMutation(
        () => updateMutation.mutateAsync({ item, payload }),
        setMutationErrorMessage,
      );
    },
    async handleDeleteOrganization(item: CrmOrganizationItem) {
      try {
        await runMutation(() => deleteMutation.mutateAsync(item), setMutationErrorMessage);
      } catch {
        // 列表删除失败时已写入 mutationErrorMessage，避免 Popconfirm 未处理 rejection。
      }
    },
    async handleCreateSite(payload: CrmOrganizationSiteMutation) {
      const nextPayload = withResolvedGeoStatus(payload, geoStatus, geoAdopted);
      await runMutation(() => createSiteMutation.mutateAsync(nextPayload), setMutationErrorMessage);
    },
    async handleUpdateSite(site: CrmOrganizationSiteItem, payload: CrmOrganizationSiteMutation) {
      const nextPayload = withResolvedGeoStatus(payload, geoStatus, geoAdopted);
      await runMutation(() => updateSiteMutation.mutateAsync({ site, payload: nextPayload }), setMutationErrorMessage);
    },
    async handleDeleteSite(site: CrmOrganizationSiteItem) {
      try {
        await runMutation(
          () => deleteSiteMutation.mutateAsync(site),
          setMutationErrorMessage,
          { failureMessage: "校区删除失败，请稍后重试。" },
        );
      } catch {
        // 校区删除失败时已写入 mutationErrorMessage。
      }
    },
    async handleAdoptDiscoveryPending(payload: CrmOrganizationSiteMutation) {
      try {
        await runMutation(() => createSiteMutation.mutateAsync(payload), setMutationErrorMessage);
        setCampusDiscoverySummary((current) => {
          if (!current) {
            return current;
          }
          const pendingItems = current.pendingItems.filter(
            (item) => item.siteName !== payload.siteName,
          );
          const pendingCount = pendingItems.length;
          return {
            ...current,
            createdCount: current.createdCount + 1,
            pendingCount,
            pendingItems,
            failureReasons: pendingItems.map((item) => item.reason),
            status: pendingCount > 0 ? "needs_confirmation" : "confirmed",
            retryable: pendingCount > 0 ? current.retryable : false,
          };
        });
      } catch {
        // 采用创建失败时已写入 mutationErrorMessage。
      }
    },
    async handleResolveGeo(
      payload: CrmOrganizationSiteMutation,
      site?: CrmOrganizationSiteItem,
    ) {
      setGeoFeedbackMessage("");
      setMutationErrorMessage("");
      setGeoStatus("processing");
      const result = await runGeoResolve(() =>
        geoResolveMutation.mutateAsync({
          province: payload.province || detailQuery.data?.province,
          city: payload.city,
          district: payload.district,
          areaName: buildGeoResolveAreaName(payload, detailQuery.data?.orgName),
          scenario: "school_campus",
          contextName: detailQuery.data?.orgName,
          sourceAddress: payload.address || undefined,
          organizationSiteCount: detailQuery.data?.sites.filter((item) => !item.isDisabled).length,
        }),
      );
      if (!result) {
        const reason = "高德识别失败，请手动选择省市区。";
        setGeoFeedbackMessage(reason);
        setGeoResult(toPanelGeoFailure(reason));
        setGeoStatus("retryable_failure");
        return payload;
      }
      setGeoResult(toPanelGeoResult(result));
      setGeoStatus(result.needsReview ? "needs_correction" : "confirmed");
      setGeoAdopted(false);
      setGeoFeedbackMessage(result.needsReview ? "GEO 识别置信度较低，请人工复核。" : result.message);
      const nextPayload = applyGeoResolveResultToSiteMutation(payload, result);
      if (site && !result.needsReview && nextPayload.lng != null && nextPayload.lat != null) {
        const persisted = { ...nextPayload, geoStatus: "confirmed" as const };
        try {
          await runMutation(
            () => updateSiteMutation.mutateAsync({ site, payload: persisted }),
            setMutationErrorMessage,
            { failureMessage: "GEO 识别结果落库失败，请稍后重试。" },
          );
          setGeoOwnerSiteId(site.id);
          setGeoAdopted(true);
          return persisted;
        } catch {
          return nextPayload;
        }
      }
      return nextPayload;
    },
    async handlePersistAdoptedSiteGeo(
      site: CrmOrganizationSiteItem,
      payload: CrmOrganizationSiteMutation,
    ) {
      if (payload.lng == null || payload.lat == null) {
        return payload;
      }
      const persisted = { ...payload, geoStatus: "confirmed" as const };
      setGeoAdopted(true);
      try {
        await runMutation(
          () => updateSiteMutation.mutateAsync({ site, payload: persisted }),
          setMutationErrorMessage,
          { failureMessage: "GEO 识别结果落库失败，请稍后重试。" },
        );
        setGeoOwnerSiteId(site.id);
        return persisted;
      } catch {
        return payload;
      }
    },
    async handleResolveOrganizationGeo(payload: CrmOrganizationMutation) {
      setGeoFeedbackMessage("");
      setMutationErrorMessage("");
      setGeoStatus("processing");
      const result = await runGeoResolve(() =>
        geoResolveMutation.mutateAsync({
          province: payload.province || undefined,
          areaName: [payload.orgName, payload.address].filter(Boolean).join(" "),
          scenario: "school_campus",
          contextName: payload.orgName,
        }),
      );
      if (!result) {
        const reason = "高德识别失败，请手动选择省市区。";
        setGeoFeedbackMessage(reason);
        setGeoResult(toPanelGeoFailure(reason));
        setGeoStatus("retryable_failure");
        return payload;
      }
      setGeoResult(toPanelGeoResult(result));
      setGeoStatus(result.needsReview ? "needs_correction" : "confirmed");
      setGeoAdopted(false);
      const nextPayload = applyGeoResolveResultToOrganizationMutation(payload, result);
      setGeoFeedbackMessage(
        nextPayload.province && nextPayload.city && nextPayload.district
          ? result.message || "省市区识别成功。"
          : "高德未能完整识别省市区，请手动选择。",
      );
      return nextPayload;
    },
    async handleRetryCampusDiscovery(organizationId: number) {
      setCampusDiscoveryOrganizationId(organizationId);
      setMutationErrorMessage("");
      try {
        await campusDiscoveryMutation.mutateAsync(organizationId);
      } catch {
        // 发现失败不回滚学校；避免沿用「保存失败」文案，也不向外抛未处理 rejection。
        setMutationErrorMessage("校区发现失败，请稍后重试。");
      }
    },
  };
}

/** 将校区识别兼容结果映射为共享 GEO 面板 DTO。 */
function toPanelGeoResult(result: {
  province: string;
  city: string;
  district: string;
  longitude?: number;
  latitude?: number;
  coordinateSystem: string;
  navigationAddress: string;
  confidenceScore: number;
  needsReview: boolean;
  message: string;
  ambiguityNotes: string;
  candidates?: GeoBusinessVerifyResult["candidates"];
}): GeoBusinessVerifyResult {
  return {
    scenario: "school_campus",
    status: result.needsReview ? "needs_correction" : "confirmed",
    conclusion: result.message,
    llmConclusion: result.ambiguityNotes,
    amapStandardAddress: result.navigationAddress,
    province: result.province,
    city: result.city,
    district: result.district,
    longitude: result.longitude,
    latitude: result.latitude,
    coordinateSystem: result.coordinateSystem,
    confidenceScore: result.confidenceScore,
    correctionReason: result.needsReview ? result.message : "",
    retryable: false,
    candidates: result.candidates ?? [],
  };
}

/** 生成共享 GEO 面板可展示的技术失败结果。 */
function toPanelGeoFailure(reason: string): GeoBusinessVerifyResult {
  return {
    scenario: "school_campus",
    status: "retryable_failure",
    conclusion: reason,
    llmConclusion: reason,
    amapStandardAddress: "",
    province: "",
    city: "",
    district: "",
    coordinateSystem: "unknown",
    confidenceScore: 0,
    correctionReason: reason,
    retryable: true,
    candidates: [],
  };
}

/** 执行变更请求并统一记录保存失败提示。 */
async function runMutation<T>(
  execute: () => Promise<T>,
  setMutationErrorMessage: (message: string) => void,
  options?: { failureMessage?: string },
) {
  setMutationErrorMessage("");
  try {
    return await execute();
  } catch (error) {
    setMutationErrorMessage(
      isHttpClientError(error) && error.code === "CRM_ORGANIZATION_NAME_CONFLICT"
        ? (
            error.message?.trim()
            && !isGenericHttpFailureMessage(error.message)
            && error.message !== "crm-organization-name-conflict"
              ? error.message
              : "学校名称已存在，请勿重复新增。"
          )
        : isHttpClientError(error) && error.code === "CRM_ORGANIZATION_DELETE_BLOCKED"
          ? (error.message.trim()
            && !isGenericHttpFailureMessage(error.message)
            && error.message !== "crm-organization-delete-blocked"
              ? error.message
              : "存在业务引用，无法删除，请先清理相关数据或改用停用。")
          : isHttpClientError(error)
            && error.code === "SEMANTIC_DUPLICATE_CHECK_FAILED"
            && error.message?.trim()
            && !isGenericHttpFailureMessage(error.message)
            ? error.message
            : (options?.failureMessage ?? "保存失败，请检查填写内容后重试。"),
    );
    throw error;
  }
}

/** 执行 GEO 请求；技术失败时由页面保留原表单并提示手动选择。 */
async function runGeoResolve<T>(execute: () => Promise<T>): Promise<T | null> {
  try {
    return await execute();
  } catch {
    return null;
  }
}

/** 把本次面板确认结果写入校区保存载荷。 */
function withResolvedGeoStatus(
  payload: CrmOrganizationSiteMutation,
  geoStatus: GeoOperationStatus,
  geoAdopted: boolean,
): CrmOrganizationSiteMutation {
  return {
    ...payload,
    geoStatus: resolvedCrmOrganizationSiteGeoStatus(payload, geoStatus, geoAdopted),
  };
}
